<template>
  <section class="subscription-capacity" :aria-busy="loading">
    <v-progress-linear v-if="loading" indeterminate :aria-label="adminText('organizationAll.ga_loading', 'Loading performance capacity')" />
    <v-alert v-if="error" :value="true" type="error">
      {{ error }}
      <v-btn flat @click="$emit('retry')">{{ adminText('organizationAll.ga_retry', 'Retry') }}</v-btn>
    </v-alert>
    <template v-if="summary && !loading && !error">
      <div class="capacity-summary">
        <v-text-field
          class="capacity-value"
          :value="summary.capacityLimit == null ? '—' : summary.capacityLimit"
          readonly
          :label="adminText('organizationAll.ga_capacity', 'Subscription capacity')"
          hide-details
        />
        <p class="capacity-description">{{ adminText('organizationAll.ga_capacity_hint', 'Lowest configured capacity among all included performances.') }}</p>
      </div>
      <v-alert v-if="summary.readiness === 'NO_EVENTS'" :value="true" type="info">
        {{ adminText('organizationAll.ga_no_events', 'No included performances. Active status alone does not enable sales.') }}
      </v-alert>
      <v-alert v-if="summary.readiness === 'INVALID_CAPACITY'" :value="true" type="warning">
        {{ adminText('organizationAll.ga_invalid_capacity', 'Every included performance needs a positive whole-number capacity before subscriptions can be sold.') }}
      </v-alert>
      <p v-if="summary.firstPerformanceAt" class="capacity-deadline">
        {{ deadlineText }}
      </p>
      <div v-if="summary.performances.length" class="performance-capacity-table">
        <table>
          <thead>
            <tr>
              <th scope="col">{{ adminText('organizationAll.ga_performance', 'Performance') }}</th>
              <th scope="col">{{ adminText('organizationAll.ga_datetime', 'Date and time') }}</th>
              <th scope="col">{{ adminText('organizationAll.ga_quantity', 'Number of tickets') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="performance in summary.performances" :key="performance.eventId">
              <td class="performance-title">{{ performance.title }}</td><td class="performance-date"><time :datetime="performance.startsAt">{{ formatPerformanceDate(performance.startsAt) }}</time></td>
              <td :data-label="adminText('organizationAll.ga_quantity', 'Number of tickets')" class="performance-quantity" :class="{ 'performance-quantity--minimum': performance.quantity === summary.capacityLimit }">{{ performance.quantity == null ? '—' : performance.quantity }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </section>
</template>
<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'
export default {
  mixins: [OrganizationLocalization],
  props: { summary: { type: Object, default: null }, loading: Boolean, error: { type: String, default: '' } },
  computed: {
    deadlineText() {
      return this.adminText('organizationAll.ga_sales_deadline', 'Sales end no later than the start of the first included performance: {{date}}', { date: this.formatPerformanceDate(this.summary.firstPerformanceAt) })
    },
  },
  methods: {
    formatPerformanceDate(value) {
      return new Date(value).toLocaleString('hr-HR', { timeZone: 'Europe/Zagreb', dateStyle: 'short', timeStyle: 'short' })
    },
  },
}
</script>
<style scoped>
.subscription-capacity { min-width: 0; color: #26343e; font-family: 'Source Sans Pro', sans-serif; }
.capacity-summary { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
.capacity-value { flex: 0 0 180px; padding-top: 0; margin-top: 0; }
.capacity-value >>> input { font-size: 20px; font-weight: 700; font-variant-numeric: tabular-nums; color: #26343e; }
.capacity-value >>> .v-label { color: #52616b; }
.capacity-description { color: #52616b; font-size: 14px; line-height: 1.5; margin: 0; }
.capacity-deadline { margin: 0 0 20px; padding: 12px 14px; background: #f4f7f9; font-size: 14px; line-height: 1.5; }
.performance-capacity-table { overflow-x: auto; }
.performance-capacity-table table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.performance-capacity-table th { padding: 10px 8px; color: #52616b; font-size: 12px; font-weight: 600; line-height: 1.4; text-align: left; border-bottom: 1px solid #d9e1e6; }
.performance-capacity-table td { padding: 14px 8px; border-bottom: 1px solid #e8edf0; font-size: 14px; line-height: 1.45; vertical-align: top; }
.performance-capacity-table th:first-child { width: 43%; }
.performance-capacity-table th:nth-child(2) { width: 35%; }
.performance-capacity-table th:last-child { width: 22%; text-align: right; }
.performance-title { overflow-wrap: anywhere; }
.performance-date { color: #52616b; font-variant-numeric: tabular-nums; }
.performance-quantity { text-align: right; font-variant-numeric: tabular-nums; }
.performance-quantity--minimum { font-weight: 700; }
@media (max-width: 599px) {
  .capacity-summary { align-items: stretch; flex-direction: column; gap: 10px; }
  .capacity-value { flex: none; width: 100%; }
  .performance-capacity-table thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
  .performance-capacity-table tr { display: grid; grid-template-columns: minmax(0, 1fr) 96px; column-gap: 16px; padding: 14px 0; border-bottom: 1px solid #e8edf0; }
  .performance-capacity-table td { padding: 0 !important; height: auto !important; border: 0; white-space: normal !important; }
  .performance-title { grid-column: 1; font-weight: 600; }
  .performance-date { grid-column: 1; margin-top: 4px; }
  .performance-quantity { grid-column: 2; grid-row: 1 / span 2; align-self: center; }
  .performance-quantity::before { content: attr(data-label); display: block; margin-bottom: 4px; color: #52616b; font-size: 12px; font-weight: 400; }
}
</style>
